import { useMemo, useState } from 'react';
import { track } from '../lib/track';
import { Icon } from './Icon';

function deterministicShuffle(items, salt = 0) {
  return [...items].sort((a, b) => {
    const score = (value) => [...value.id].reduce((sum, character) => sum + character.charCodeAt(0), salt) % 997;
    return score(a) - score(b);
  });
}

export function Quiz({ questions = [], title, description, limit = 8, loading = false }) {
  const [attempt, setAttempt] = useState(0);
  const [started, setStarted] = useState(false);
  const [index, setIndex] = useState(0);
  const [selected, setSelected] = useState('');
  const [score, setScore] = useState(0);
  const [finished, setFinished] = useState(false);
  const sessionQuestions = useMemo(() => deterministicShuffle(questions, attempt).slice(0, limit), [attempt, limit, questions]);
  const question = sessionQuestions[index];

  const start = () => {
    setAttempt((value) => value + 1);
    setStarted(true);
    setIndex(0);
    setSelected('');
    setScore(0);
    setFinished(false);
  };

  if (!started) {
    return (
      <section className="quiz-intro panel-card">
        <span className="eyebrow">Test wat je al kent</span>
        <h2>{title}</h2>
        <p>{description}</p>
        <div className="quiz-types"><span>Volgorde</span><span>Herkenning</span><span>Betekenis</span></div>
        <button className="button button-primary" type="button" onClick={start} disabled={loading || !questions.length}>{loading ? 'Vragen laden…' : 'Start de quiz'} {!loading && <Icon name="arrow" />}</button>
      </section>
    );
  }

  if (finished) {
    const percentage = Math.round((score / sessionQuestions.length) * 100);
    return (
      <section className="quiz-result panel-card" aria-live="polite">
        <div className="score-circle"><strong>{score}/{sessionQuestions.length}</strong><span>{percentage}% goed</span></div>
        <h2>{percentage >= 80 ? 'MashaAllah, sterk geoefend!' : percentage >= 50 ? 'Goed bezig — nog één ronde?' : 'Elke oefenronde telt'}</h2>
        <p>{percentage >= 80 ? 'Je herkent de verzen en betekenissen al goed.' : 'Lees de verzen nog eens rustig terug en probeer daarna opnieuw.'}</p>
        <button className="button button-primary" type="button" onClick={start}><Icon name="refresh" /> Opnieuw proberen</button>
      </section>
    );
  }

  const answerIsCorrect = selected === question.answer;
  const choose = (option) => {
    if (selected) return;
    setSelected(option);
    if (option === question.answer) setScore((value) => value + 1);
  };
  const next = () => {
    if (index + 1 >= sessionQuestions.length) {
      track('quiz_finish', { label: title, value: Math.round((score / sessionQuestions.length) * 100) });
      setFinished(true);
    } else {
      setIndex((value) => value + 1);
      setSelected('');
    }
  };

  return (
    <section className="quiz-card panel-card" aria-labelledby="quiz-vraag">
      <div className="quiz-progress"><span>Vraag {index + 1} van {sessionQuestions.length}</span><strong>{score} goed</strong><div><i style={{ width: `${((index + (selected ? 1 : 0)) / sessionQuestions.length) * 100}%` }} /></div></div>
      <p className="question-label" id="quiz-vraag">{question.promptLabel}</p>
      <p className="question-prompt" dir="rtl" lang="ar">{question.prompt}</p>
      <div className={`quiz-options ${question.type === 'next' ? 'arabic-options' : ''}`}>
        {question.options.map((option) => {
          const correctOption = selected && option === question.answer;
          const wrongOption = selected === option && option !== question.answer;
          return (
            <button type="button" key={option} onClick={() => choose(option)} disabled={Boolean(selected)} className={`${correctOption ? 'is-correct' : ''} ${wrongOption ? 'is-wrong' : ''}`} dir={question.type === 'next' ? 'rtl' : undefined}>
              <span>{option}</span>{correctOption && <Icon name="check" />}{wrongOption && <Icon name="close" />}
            </button>
          );
        })}
      </div>
      {selected && (
        <div className={`answer-feedback ${answerIsCorrect ? 'correct' : 'wrong'}`} role="status">
          <strong>{answerIsCorrect ? 'Goed!' : 'Nog niet.'}</strong>
          <p>{question.explanation}</p>
          <button className="button button-primary" type="button" onClick={next}>{index + 1 === sessionQuestions.length ? 'Bekijk score' : 'Volgende vraag'} <Icon name="arrow" /></button>
        </div>
      )}
    </section>
  );
}
